/**
 * Auth API service layer.
 * All auth-related HTTP calls go through here.
 */
import { apiClient } from "@services/apiClient";

export interface LoginPayload {
  email: string;
  password: string;
  totp_code?: string;
}

export interface RegisterPayload {
  email: string;
  nickname?: string;
  nicknameRarity?: string;
  nicknameScore?: number;
  displayName?: string;
  password: string;
  confirmPassword: string;
}

export interface NicknameResult {
  nickname: string;
  rarity: string;
  rarity_label: string;
  rarity_color: string;
  score: number;
}

export const authApi = {
  login: (payload: LoginPayload) => apiClient.post("/auth/login/", payload),
  register: (payload: RegisterPayload) =>
    apiClient.post("/auth/register/", {
      email: payload.email,
      ...(payload.nickname ? { nickname: payload.nickname } : {}),
      ...(payload.nicknameRarity ? { nickname_rarity: payload.nicknameRarity } : {}),
      ...(typeof payload.nicknameScore === "number" ? { nickname_score: payload.nicknameScore } : {}),
      display_name: payload.displayName,
      password: payload.password,
      confirm_password: payload.confirmPassword,
    }),
  logout: (refresh: string) => apiClient.post("/auth/logout/", { refresh }),
  refreshToken: (refresh: string) => apiClient.post("/auth/token/refresh/", { refresh }),
  verifyEmail: (token: string) => apiClient.get(`/auth/verify-email/${token}/`),
  requestPasswordReset: (email: string) => apiClient.post("/auth/password/reset/", { email }),
  confirmPasswordReset: (token: string, newPassword: string) =>
    apiClient.post("/auth/password/reset/confirm/", { token, new_password: newPassword, confirm_password: newPassword }),
  changePassword: (oldPassword: string, newPassword: string) =>
    apiClient.post("/auth/password/change/", {
      old_password: oldPassword,
      new_password: newPassword,
      confirm_new_password: newPassword,
    }),
  setup2FA: () => apiClient.get("/auth/2fa/setup/"),
  enable2FA: (code: string) => apiClient.post("/auth/2fa/enable/", { code }),
  disable2FA: (code: string) => apiClient.post("/auth/2fa/disable/", { code }),

  // ── Email OTP ─────────────────────────────────────────────────────────────
  /** Send a 6-digit OTP to the given email address. */
  sendEmailOtp: (email: string, purpose: "register" | "login" = "register") =>
    apiClient.post("/auth/otp/send/", { email, purpose }),

  /** Verify the 6-digit OTP the user typed in. */
  verifyEmailOtp: (email: string, code: string, purpose: "register" | "login" = "register") =>
    apiClient.post("/auth/otp/verify/", { email, code, purpose }),
};

// ── Nickname API (public – no auth required) ──────────────────────────────
export const nicknameApi = {
  /** Generate a new random nickname. */
  generate: () =>
    apiClient.get<{ success: boolean; data: NicknameResult }>("/nicknames/generate/"),

  /** Check if a specific nickname is still available. */
  check: (nickname: string) =>
    apiClient.post<{ success: boolean; data: { available: boolean } }>("/nicknames/check/", { nickname }),
};
